import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Building2, 
  MessageSquare, 
  Eye, 
  TrendingUp,
  Plus,
  ArrowRight,
  ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DashboardHome() {
  const { agency, getAuthHeader } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentEnquiries, setRecentEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [analyticsRes, enquiriesRes] = await Promise.all([
        axios.get(`${API_URL}/api/analytics`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/enquiries`, { headers: getAuthHeader() })
      ]);
      setStats(analyticsRes.data);
      setRecentEnquiries(enquiriesRes.data.enquiries.slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-navy-200 rounded w-1/4" />
          <div className="grid grid-cols-4 gap-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-navy-200 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const statCards = [
    { 
      title: 'Total Properties', 
      value: stats?.total_properties || 0, 
      icon: Building2,
      color: 'bg-sky-500',
      link: '/dashboard/properties'
    },
    { 
      title: 'Total Enquiries', 
      value: stats?.total_enquiries || 0, 
      icon: MessageSquare,
      color: 'bg-green-500',
      link: '/dashboard/enquiries'
    },
    { 
      title: 'Page Views', 
      value: stats?.total_page_views || 0, 
      icon: Eye,
      color: 'bg-purple-500',
      link: '/dashboard/analytics'
    },
    { 
      title: 'This Month', 
      value: stats?.enquiries_this_month || 0, 
      icon: TrendingUp,
      color: 'bg-orange-500',
      subtitle: 'enquiries',
      link: '/dashboard/analytics'
    },
  ];

  return (
    <div className="p-6 lg:p-8" data-testid="dashboard-home">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-heading font-bold text-navy-900">
            Welcome back, {agency?.company_name}
          </h1>
          <p className="text-navy-600">Here's what's happening with your properties</p>
        </div>
        <div className="flex gap-3">
          <Link to={`/agency/${agency?.slug}`} target="_blank">
            <Button variant="outline" className="gap-2" data-testid="view-website-btn">
              <ExternalLink className="w-4 h-4" />
              View Website
            </Button>
          </Link>
          <Link to="/dashboard/properties/new">
            <Button className="bg-navy-900 hover:bg-navy-800 gap-2" data-testid="add-property-btn">
              <Plus className="w-4 h-4" />
              Add Property
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat) => (
          <Link to={stat.link} key={stat.title}>
            <Card className="hover:shadow-lg transition-shadow cursor-pointer" data-testid={`stat-${stat.title.toLowerCase().replace(' ', '-')}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-navy-600 mb-1">{stat.title}</p>
                    <p className="text-3xl font-bold text-navy-900">{stat.value}</p>
                    {stat.subtitle && (
                      <p className="text-xs text-navy-500 mt-1">{stat.subtitle}</p>
                    )}
                  </div>
                  <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Quick Actions & Recent Enquiries */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <Card data-testid="quick-actions-card">
          <CardHeader>
            <CardTitle className="text-lg">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/dashboard/properties/new" className="block">
              <div className="flex items-center justify-between p-4 bg-navy-50 rounded-lg hover:bg-navy-100 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-sky-500 rounded-lg flex items-center justify-center">
                    <Plus className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-navy-900">Add New Property</p>
                    <p className="text-sm text-navy-600">List a new property on your website</p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-navy-400" />
              </div>
            </Link>
            <Link to="/dashboard/settings" className="block">
              <div className="flex items-center justify-between p-4 bg-navy-50 rounded-lg hover:bg-navy-100 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-navy-900">Update Profile</p>
                    <p className="text-sm text-navy-600">Edit your company information</p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-navy-400" />
              </div>
            </Link>
          </CardContent>
        </Card>

        {/* Recent Enquiries */}
        <Card data-testid="recent-enquiries-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Recent Enquiries</CardTitle>
            <Link to="/dashboard/enquiries">
              <Button variant="ghost" size="sm" className="text-sky-500">
                View All
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {recentEnquiries.length === 0 ? (
              <div className="text-center py-8 text-navy-500">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 text-navy-300" />
                <p>No enquiries yet</p>
                <p className="text-sm">Share your website to start receiving enquiries</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentEnquiries.map((enquiry) => (
                  <div 
                    key={enquiry.id} 
                    className={`p-3 rounded-lg border ${enquiry.read ? 'bg-white border-navy-100' : 'bg-sky-50 border-sky-200'}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-navy-900">{enquiry.name}</p>
                        <p className="text-sm text-navy-600">{enquiry.property_title || 'General Enquiry'}</p>
                      </div>
                      {!enquiry.read && (
                        <span className="px-2 py-1 bg-sky-500 text-white text-xs rounded-full">New</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
